import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, Observable, Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IAgent } from 'src/app/features/agents/models/agents.models';
import * as AgentSelectors from 'src/app/features/agents/store/agents.selectors';
import { CatalogsService } from '../../../services/catalogs.services';
import { GetAgents } from 'src/app/features/agents/store/agents.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-new-catalog-agents',
  templateUrl: './new-catalog-agents.component.html',
  styleUrls: ['./new-catalog-agents.component.scss']
})
export class NewCatalogAgentsComponent implements OnInit {
  agents$: Observable<IAgent[]> = this.store.select(
    AgentSelectors.selectAgents
  );
  agents: IAgent[] = [];
  filteredAgents: IAgent[] = [];

  agentId!: number;
  selectedAgents: number[] = []
  assignAll: boolean = false;

  searchInput = new FormControl('');
  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;

  destroy$ = new Subject<void>();
  canCreate = false;
  canEdit = false;
  isEditMode = false;

  constructor(private route: ActivatedRoute, private store: Store<AppState>, private catalogService: CatalogsService) { }

  ngOnInit(): void {
    const data = this.catalogService.getNewCatalogData();
    this.isEditMode = !!data?.catalogtId;
    this.store.select(selectCanDo('catalog', 'create')).pipe(takeUntil(this.destroy$)).subscribe((canCreate) => {
      this.canCreate = canCreate;
    });
    this.store.select(selectCanDo('catalog', 'edit')).pipe(takeUntil(this.destroy$)).subscribe((canEdit) => {
      this.canEdit = canEdit;
    });

    combineLatest([
      this.agents$.pipe(
        filter((agents) => agents.length > 0),
        distinctUntilChanged()
      ),
      this.route.queryParams
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([agents, params]) => {
      this.agents = agents;
      this.filteredAgents = [...agents];
  
      this.agentId = Number(params['agentId']);
  
      if (this.agentId) {
        this.selectedAgents.push(this.agentId);
  
        if (!data?.agents?.length) {
          this.catalogService.setNewCatalogData({
            agents: agents.filter((agent: IAgent) => this.agentId === agent.id!)
          });
        }
      }
  
      this.selectedAgents = data && data.agents ? data.agents.map((agent) => agent.id!) : [];
      this.assignAll = this.selectedAgents.length === this.agents.length && this.agents.length > 0;
    });

    this.store.dispatch(GetAgents({subsidiary_id: data?.subsidiary_id!}));
  }

  getAgentFullname(agent: IAgent) {
    return agent.first_name.toLowerCase() + ' ' +  agent.last_name.toLowerCase();
  }

  search(event: any) {
    const key = event.target.value.toLowerCase();
    if (key !== '') {
      this.filteredAgents = this.agents.filter((agent: IAgent) => {
        const agentFullName = this.getAgentFullname(agent);
        return agentFullName.includes(key)
      });
    } else {
      this.filteredAgents = [...this.agents];
    }

    this.activeSortKey = null;
    this.activeSortMode = null;
  }

  agentIsChecked(agent: IAgent) {
    return this.selectedAgents.includes(agent.id!);
  }

  toggleAgent(agent: IAgent) {
    if (!this.canSubmit) return;
    if(!this.agentIsChecked(agent)) {
      this.selectedAgents.push(agent.id!);
    } else {
      this.selectedAgents = this.selectedAgents.filter(
        (id) => id !== agent.id!
      );
    }

    this.assignAll = this.selectedAgents.length === this.agents.length && this.agents.length > 0;

    this.catalogService.setNewCatalogData({agents: this.agents.filter((agent: IAgent) => this.selectedAgents.includes(agent.id!))});
  }

  toggleAssignAll() {
    if (!this.canSubmit) return;
    this.assignAll = !this.assignAll;
    
    if (this.assignAll) {
      this.selectedAgents = this.agents.map((agent) => agent.id!);
      this.catalogService.setNewCatalogData({agents: [...this.agents]});
    } else {
      this.selectedAgents = [];
      this.catalogService.setNewCatalogData({agents: []});
    }
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (key === this.activeSortKey && sortDirection === this.activeSortMode) return;
    if (!this.filteredAgents || !Array.isArray(this.filteredAgents)) return;

    const isEmpty = (v: any) => v === null || v === undefined || v === '';

    const isStrictDateString = (val: string): boolean => {
      // Matches YYYY-MM-DD, YYYY/MM/DD, with optional time (space or T separator)
      return /^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}:\d{2})?$/.test(val);
    }
    
    const compareValues = (a: any, b: any): number => {
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;

      const isDateA = typeof a === 'string' && isStrictDateString(a);
      const isDateB = typeof b === 'string' && isStrictDateString(b);

      if (isDateA || isDateB) {
        return new Date(a).getTime() - new Date(b).getTime();
      }

      if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      }

      return String(a).localeCompare(String(b));
    };

    this.filteredAgents = [...this.filteredAgents].sort(
      (itemA: any, itemB: any) => {
        let valA: any = itemA[key];
        let valB: any = itemB[key];

        if (key === 'name') {
          valA = this.getAgentFullname(itemA) ?? '';
          valB = this.getAgentFullname(itemB) ?? '';
        }

        const result = compareValues(valA, valB);
        return sortDirection === 'asc' ? result : -result;
      }
    );

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
  }

  get canSubmit(): boolean {
    return this.isEditMode ? this.canEdit : this.canCreate;
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.isEditMode ? 'edit' : 'create' };
  }
}
